import BottomSheet, {
  BottomSheetBackdrop,
  BottomSheetBackdropProps,
  BottomSheetScrollView,
} from '@gorhom/bottom-sheet'
import { Id, Resource } from '@nextforce/api'
import { makeStyles } from '@rneui/themed'
import {
  FileFolderSortOption,
  FileFolderViewMode,
} from 'file_folder_repository/types'
import React, { useCallback } from 'react'
import { Platform } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { OptionsView } from './document_list_options_bottom_sheet'
import { FileFolderDetailsView } from './file_folder_details_bottom_sheet'

export type FileFolderModalType = 'options' | 'details'

interface Props {
  type: FileFolderModalType
  itemId: Id | undefined
  itemType: Resource['type'] | undefined
  onPressAction(resource: Resource, action: string): void
  onPressThumbnail(): void
  onClose(): void
  onPressSortOption(option: FileFolderSortOption): void
  onPressViewMode(option: FileFolderViewMode): void
  selectedSortOption: FileFolderSortOption
  selectedViewMode: FileFolderViewMode
}

export const FileFolderBottomSheet = React.forwardRef<BottomSheet, Props>(
  (
    {
      type,
      itemId,
      itemType,
      onClose,
      onPressAction,
      onPressThumbnail,
      onPressSortOption,
      onPressViewMode,
      selectedSortOption,
      selectedViewMode,
    },
    ref
  ) => {
    const styles = useStyles()
    const { bottom } = useSafeAreaInsets()

    const renderBackdrop = useCallback(
      (props: BottomSheetBackdropProps) => (
        <BottomSheetBackdrop
          {...props}
          disappearsOnIndex={-1}
          appearsOnIndex={0}
        />
      ),
      []
    )

    return (
      <BottomSheet
        ref={ref}
        index={0}
        onClose={onClose}
        backdropComponent={renderBackdrop}
        snapPoints={['80%']}
        enablePanDownToClose
        enableDynamicSizing={true}
        animateOnMount={Platform.OS !== 'ios'}
        handleIndicatorStyle={styles.bottomSheetHandleIndicator}
        backgroundStyle={styles.bottomSheetBackground}
      >
        <BottomSheetScrollView
          contentContainerStyle={{ paddingBottom: bottom }}
        >
          {type === 'options' ? (
            <OptionsView
              onPressSortOption={onPressSortOption}
              onPressViewMode={onPressViewMode}
              selectedSortOption={selectedSortOption}
              selectedViewMode={selectedViewMode}
            />
          ) : (
            <FileFolderDetailsView
              itemId={itemId}
              itemType={itemType}
              onPressAction={onPressAction}
              onPressThumbnail={onPressThumbnail}
            />
          )}
        </BottomSheetScrollView>
      </BottomSheet>
    )
  }
)

const useStyles = makeStyles(theme => ({
  detailsContainer: {
    paddingBottom: 10,
    flex: 1,
  },
  container: { flex: 1 },
  bottomSheetBackground: {
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey11 : theme.colors.grey6,
  },
  bottomSheetHandleIndicator: {
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.black : theme.colors.grey4,
  },
  headerText: {
    marginLeft: 15,
    fontWeight: 'bold',
    color: 'grey',
    marginBottom: 5,
  },
  sortOptionContainer: {
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey7 : theme.colors.white,
  },
  sortOptionTitleContainer: { margin: 0, padding: 0 },
  sortOptionCheckbox: {
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey7 : theme.colors.white,
  },
  collapsibleContainer: {
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey11 : theme.colors.grey6,
  },
  viewModeItemsContainer: {
    flex: 1,
    paddingBottom: 10,
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey7 : theme.colors.white,
  },
  viewModeItem: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 10,
  },
}))
